import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import styled from 'styled-components';
import { Menubar as PrimeReactMenuBar } from 'primereact/menubar';
import ConfirmDialog from 'components/UI/ConfirmDialog';
import Text from 'components/UI/Text';
import { useStores } from 'stores/hooks/hooks';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid/Grid';
import { MenuItem } from 'primereact/menuitem';
import { AREA_UNITS } from 'core/constants';
import { ActionMode } from 'models/shared';
import { useApolloClient } from '@apollo/client';
import { exportArrayToCSV } from 'utils/fileHelpers';
import { GqlPlotAlertsPaginated, GqlPlotSensorsSystems } from 'gql/types/plots';
import {
  handleCreateAnomalyData,
  handleCreateTechnicalData,
} from '../AlertsTable.utils';

const StyledPrimeReactMenuBar = styled(PrimeReactMenuBar)`
  .p-submenu-list {
    width: auto;
  }

  .p-menuitem-text {
    text-wrap: nowrap;
  }
`;

interface MenuBarProps {
  query: any;
  fields?: { fieldKey: string; name: string }[];
  tableType: 'technical' | 'anomaly';
}

const Menubar: FC<MenuBarProps> = ({ query, fields, tableType }) => {
  const [loading, setLoading] = useState(false);
  const { resellersStore } = useStores();
  const growerIds = resellersStore.selectedGrowerId;
  const { t } = useTranslation('liveMonitoring');
  const { t: alertT } = useTranslation('alertTypes');

  const client = useApolloClient();

  const [menuModel, setMenuModel] = useState<MenuItem[]>([]);

  const downloadCsvTechincal = useCallback(async () => {
    setLoading(true);
    try {
      const dataAlerts = client.query({
        query: query.query,
        variables: query.variables,
        fetchPolicy: 'no-cache',
      });

      const dataSensors = client.query({
        query: query.querySensors,
        variables: query.variablesSensors,
        fetchPolicy: 'no-cache',
      });

      const [dataAlertsResult, dataSensorsResult] = await Promise.all([
        dataAlerts,
        dataSensors,
      ]);

      const preparedData = handleCreateTechnicalData(
        dataAlertsResult?.data as GqlPlotAlertsPaginated,
        dataSensorsResult?.data as GqlPlotSensorsSystems,
        { t: alertT },
      );

      if (preparedData?.pageAlerts?.content?.length) {
        exportArrayToCSV(
          preparedData?.pageAlerts.content as any,
          `technical_alerts_${growerIds}.csv`,
          fields || [],
        );
      }
    } catch (error) {
      console.error('Error fetching data for CSV download:', error);
    } finally {
      setLoading(false);
    }
  }, [
    alertT,
    client,
    fields,
    growerIds,
    query.query,
    query.querySensors,
    query.variables,
    query.variablesSensors,
  ]);

  const downloadAnomalyCSV = useCallback(async () => {
    setLoading(true);
    try {
      const dataAlerts = client.query({
        query: query.query,
        variables: query.variables,
        fetchPolicy: 'no-cache',
      });

      const [dataAlertsResult] = await Promise.all([dataAlerts]);

      const preparedData = handleCreateAnomalyData(dataAlertsResult?.data);

      if (preparedData?.anomalyDetection?.content?.length) {
        exportArrayToCSV(
          preparedData?.anomalyDetection.content as any,
          `anomaly_alerts_${growerIds}.csv`,
          fields || [],
        );
      }
    } catch (error) {
      console.error('Error fetching data for CSV download:', error);
    } finally {
      setLoading(false);
    }
  }, [client, growerIds, fields, query.query, query.variables]);

  const alertItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('download_csv'),
        icon: loading ? 'pi pi-spin pi-spinner' : 'pi pi-fw pi-download',
        className: loading ? 'p-disabled' : '',
        command: () => {
          switch (tableType) {
            case 'technical':
              downloadCsvTechincal();
              break;
            case 'anomaly':
              downloadAnomalyCSV();
              break;
            default:
              break;
          }
        },
      },
    ],
    [t, loading, tableType, downloadCsvTechincal, downloadAnomalyCSV],
  );

  useEffect(() => {
    setMenuModel(alertItems);
  }, [alertItems]);

  return (
    <div>
      <StyledPrimeReactMenuBar model={menuModel} />
    </div>
  );
};

export default observer(Menubar);
